import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './App.scss';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { initGA, trackPageView } from './utils/analytics';
import Navigation from './components/Navigation';
import CookieConsent from './components/CookieConsent';
import { CartProvider } from './context/CartContext';

// Pages
import Home from './pages/Home';
import Services from './pages/Services';
import Solutions from './pages/Solutions';
import Security from './pages/Solutions/Security';
import Infrastructure from './pages/Solutions/Infrastructure';
import FinOps from './pages/Solutions/FinOps';
import Compliance from './pages/Solutions/Compliance';
import AISecurity from './pages/Solutions/AISecurity';
import CaseStudies from './pages/CaseStudies';
import FinTechPlatform from './pages/CaseStudies/FinTechPlatform';
import MLAIPlatform from './pages/CaseStudies/MLAIPlatform';
import Blog from './pages/Blog';
import ContactUs from './pages/ContactUs';
import About from './pages/About';
import NotFound from './pages/NotFound';
import Brochure from './pages/Brochure';
import ShoppingCartPage from './pages/ShoppingCart';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CloudSecuritySeries from './pages/Blog/CloudSecuritySeries';
import WhatIsCloudSecurityPosture from './pages/Blog/CloudSecuritySeries/WhatIsCloudSecurityPosture';
import AccessControl from './pages/Blog/CloudSecuritySeries/AccessControl';
import DenyByDefault from './pages/Blog/CloudSecuritySeries/DenyByDefault';
import DataEncryption from './pages/Blog/CloudSecuritySeries/DataEncryption';
import DataBackupAndRecovery from './pages/Blog/CloudSecuritySeries/DataBackupAndRecovery';
import VulnerabilityManagementSystem from './pages/Blog/CloudSecuritySeries/VulnerabilityManagementSystem';
import VulnerabilityManagementSystemContainers from './pages/Blog/CloudSecuritySeries/VulnerabilityManagementSystemContainers';
import VulnerabilityManagementSystemRuntime from './pages/Blog/CloudSecuritySeries/VulnerabilityManagementSystemRuntime';
import AntivirusScanningAndSecurityPatching from './pages/Blog/CloudSecuritySeries/AntivirusScanningAndSecurityPatching';
import Observability from './pages/Blog/CloudSecuritySeries/Observability';
import SOC2LeanStartups from './pages/Blog/SOC2LeanStartups';
import IAM from './pages/Blog/SOC2LeanStartups/IAM';

// Components
import Footer from './components/Footer';

// Analytics wrapper component
const AnalyticsWrapper = ({ children }) => {
  const location = useLocation();
  const [isAnalyticsInitialized, setIsAnalyticsInitialized] = React.useState(false);

  useEffect(() => {
    let isMounted = true;

    // Initialize GA when component mounts
    const initializeAnalytics = async () => {
      try {
        await initGA();
        if (isMounted) {
          setIsAnalyticsInitialized(true);
        }
      } catch (error) {
        // Only log error if component is still mounted
        if (isMounted) {
          console.error('Failed to initialize analytics:', error);
          setIsAnalyticsInitialized(false);
        }
      }
    };

    initializeAnalytics();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Only track page views if analytics is initialized
    if (isAnalyticsInitialized) {
      // Track page views when route changes, but only after a small delay
      // to ensure proper initialization
      const timeoutId = setTimeout(() => {
        trackPageView(location.pathname + location.search);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [location, isAnalyticsInitialized]);

  return children;
};

const App = () => {
  return (
    <CartProvider>
      <Router>
        <AnalyticsWrapper>
          <div className="App">
            <Navigation />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/solutions/security" element={<Security />} />
                <Route path="/solutions/infrastructure" element={<Infrastructure />} />
                <Route path="/solutions/finops" element={<FinOps />} />
                <Route path="/solutions/compliance" element={<Compliance />} />
                <Route path="/solutions/ai-security" element={<AISecurity />} />
                <Route path="/case-studies" element={<CaseStudies />} />
                <Route path="/case-studies/fintech-platform" element={<FinTechPlatform />} />
                <Route path="/case-studies/ml-ai-platform" element={<MLAIPlatform />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/cloud-security-series" element={<CloudSecuritySeries />} />
                <Route
                  path="/blog/cloud-security-series/what-is-a-cloud-security-posture"
                  element={<WhatIsCloudSecurityPosture />}
                />
                <Route
                  path="/blog/cloud-security-series/access-control"
                  element={<AccessControl />}
                />
                <Route
                  path="/blog/cloud-security-series/deny-by-default"
                  element={<DenyByDefault />}
                />
                <Route
                  path="/blog/cloud-security-series/data-encryption"
                  element={<DataEncryption />}
                />
                <Route
                  path="/blog/cloud-security-series/data-backup-and-recovery"
                  element={<DataBackupAndRecovery />}
                />
                <Route
                  path="/blog/cloud-security-series/vulnerability-management-system"
                  element={<VulnerabilityManagementSystem />}
                />
                <Route
                  path="/blog/cloud-security-series/vulnerability-management-system-containers"
                  element={<VulnerabilityManagementSystemContainers />}
                />
                <Route
                  path="/blog/cloud-security-series/vulnerability-management-system-runtime"
                  element={<VulnerabilityManagementSystemRuntime />}
                />
                <Route
                  path="/blog/cloud-security-series/antivirus-scanning-and-security-patching"
                  element={<AntivirusScanningAndSecurityPatching />}
                />
                <Route
                  path="/blog/cloud-security-series/observability"
                  element={<Observability />}
                />
                <Route path="/blog/soc2-for-lean-startups" element={<SOC2LeanStartups />} />
                <Route path="/blog/soc2-for-lean-startups/iam" element={<IAM />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/about" element={<About />} />
                <Route path="/brochure" element={<Brochure />} />
                <Route path="/cart" element={<ShoppingCartPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
            <CookieConsent />
          </div>
        </AnalyticsWrapper>
      </Router>
    </CartProvider>
  );
};

export default App;
